import { isLocalPath } from '../nav/localPath';
/**
 * "Wybierz z katalogu" (owner 2026-10-21 00:32): the quote sends the person to the catalog (or the suppliers'
 * catalogs) in a picking mode; a click on a position comes back to the quote with what to add. Everything lives in
 * the address, so "Wstecz" and a reload keep working.
 */

/** Where the picked position goes: the quote's draft, its section, and the quote's own address to come back to. */
export type QuotePickTarget = { versionId: string; sectionId: string; back: string };
/** Owner 2026-10-24 11:15 („tak samo jak w kosztorysie”): the packlist of an event, and its address to come back to. */
export type PacklistPickTarget = { eventId: string; back: string };
export type PickTarget = QuotePickTarget | PacklistPickTarget;
export const isPacklistPick = (t: PickTarget): t is PacklistPickTarget => 'eventId' in t;
/** What comes back: our item, or a position of a supplier's catalog. */
export type PickedKind = 'item' | 'dostawca';

/** The catalog's (or the suppliers' catalogs') address in the picking mode. */
export function pickUrl(base: '/katalog' | '/katalog/dostawcy' | string, t: PickTarget): string {
  const q = isPacklistPick(t) ? new URLSearchParams({ wybierz: '1', sprzetowka: t.eventId, powrot: t.back }) : new URLSearchParams({ wybierz: '1', kosztorys: t.versionId, sekcja: t.sectionId, powrot: t.back });
  return `${base}?${q.toString()}`;
}

/** The picking mode of a catalog page, or null (a normal visit). */
export function pickTarget(params: URLSearchParams): PickTarget | null {
  const versionId = params.get('kosztorys');
  const sectionId = params.get('sekcja');
  const eventId = params.get('sprzetowka');
  const back = params.get('powrot');
  // review: only an address of the app („//host” or „/\host” would leave it — #151)
  if (params.get('wybierz') !== '1' || !back || !isLocalPath(back)) return null;
  if (eventId) return { eventId, back };
  if (!versionId || !sectionId) return null;
  return { versionId, sectionId, back };
}

/** Keeps the picking mode when moving between catalog pages (the suppliers' list → one supplier). */
export const pickSearch = (t: PickTarget | null): string => (t ? pickUrl('', t) : '');

/** The quote's (packlist's) address with what to add (`dodaj=item:<id>` or `dodaj=dostawca:<priceId>`; the quote's `sekcja`). */
export function returnUrl(t: PickTarget, picked: { kind: PickedKind; id: string }): string {
  const [path, query = ''] = t.back.split('?');
  const q = new URLSearchParams(query);
  q.set('dodaj', `${picked.kind}:${picked.id}`);
  if (!isPacklistPick(t)) q.set('sekcja', t.sectionId);
  return `${path}?${q.toString()}`;
}

/** What the quote was asked to add on its way back, or null. */
export function parseAdd(params: URLSearchParams): { kind: PickedKind; id: string; sectionId: string } | null {
  const raw = params.get('dodaj');
  const sectionId = params.get('sekcja') ?? '';
  const m = raw ? /^(item|dostawca):([\w-]{1,64})$/.exec(raw) : null;
  if (!m) return null;
  return { kind: m[1] as PickedKind, id: m[2]!, sectionId };
}
